import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  Ref,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import { Box, IconButton, Slider, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ReactPlayer from "react-player";

import { VideoPlayerState } from "../types/VideoPlayerState";

interface AudioPlayerProps {
  src: string;
  videoPlayerState: VideoPlayerState;
  onUpdatePlayerState: (state: VideoPlayerState) => void;
}

export interface AudioPreviewPlayerHandle {
  seekTo: (time: number) => void;
  play: () => void;
  pause: () => void;
  isPlaying: () => boolean;
}

const fmt = (s: number): string => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const BAR_COUNT = 100;
const BAR_GAP = 2;

// Park-Miller PRNG constants for deterministic waveform generation
const PRNG_MULTIPLIER = 16807;
const PRNG_MODULUS = 2147483647;

function generateWaveformData(count: number): number[] {
  const data: number[] = [];
  let seed = 42;
  for (let i = 0; i < count; i += 1) {
    seed = (seed * PRNG_MULTIPLIER) % PRNG_MODULUS;
    const normalized = seed / PRNG_MODULUS;
    const segmentPhase = Math.sin((i / count) * Math.PI * 6) * 0.15;
    const envelope = Math.sin((i / count) * Math.PI) * 0.25 + 0.75;
    data.push(
      Math.max(
        0.08,
        Math.min(1, (normalized * 0.6 + 0.2 + segmentPhase) * envelope),
      ),
    );
  }
  return data;
}

const AudioPreviewPlayer = forwardRef<
  AudioPreviewPlayerHandle,
  AudioPlayerProps
>(
  (
    { src, videoPlayerState, onUpdatePlayerState }: AudioPlayerProps,
    ref: Ref<AudioPreviewPlayerHandle>,
  ) => {
    const playerRef = useRef<ReactPlayer>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animFrameRef = useRef<number>(0);
    const { isPlaying } = videoPlayerState;

    const [duration, setDuration] = useState(0);
    const [played, setPlayed] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [muted, setMuted] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const waveformData = useMemo(() => generateWaveformData(BAR_COUNT), []);

    const drawWaveform = useCallback(
      (timestamp: number = 0) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const w = rect.width;
        const h = rect.height;
        const centerY = h / 2;
        const maxBarHeight = h * 0.55;
        const totalGaps = (BAR_COUNT - 1) * BAR_GAP;
        const barWidth = Math.max(1.5, (w - totalGaps) / BAR_COUNT);
        const progress = duration > 0 ? played / duration : 0;

        ctx.clearRect(0, 0, w, h);

        for (let i = 0; i < BAR_COUNT; i += 1) {
          let amplitude = waveformData[i];

          if (isPlaying) {
            const wave1 = Math.sin(timestamp * 0.003 + i * 0.4) * 0.06;
            const wave2 = Math.sin(timestamp * 0.005 + i * 0.7) * 0.04;
            const wave3 = Math.sin(timestamp * 0.002 + i * 1.1) * 0.03;
            amplitude = Math.max(
              0.06,
              Math.min(1, amplitude + wave1 + wave2 + wave3),
            );
          }

          const barHeight = amplitude * maxBarHeight;
          const x = i * (barWidth + BAR_GAP);
          const barProgress = i / BAR_COUNT;
          const isBarPlayed = barProgress <= progress;

          const topY = centerY - barHeight;
          const fullHeight = barHeight * 2;
          const radius = Math.min(barWidth / 2, 1.5);

          ctx.fillStyle = isBarPlayed
            ? "rgba(59, 130, 246, 0.85)"
            : "rgba(203, 213, 225, 0.7)";

          ctx.beginPath();
          ctx.roundRect(x, topY, barWidth, fullHeight, radius);
          ctx.fill();
        }

        // Playhead indicator (vertical line + circle)
        if (progress > 0 && progress < 1) {
          const playheadX = w * progress;
          const lineTop = centerY - maxBarHeight - 6;
          const lineBottom = centerY + maxBarHeight + 6;

          ctx.beginPath();
          ctx.moveTo(playheadX, lineTop);
          ctx.lineTo(playheadX, lineBottom);
          ctx.strokeStyle = "rgba(59, 130, 246, 0.8)";
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(playheadX, lineTop, 5, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(59, 130, 246, 0.9)";
          ctx.fill();
          ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        if (isPlaying) {
          animFrameRef.current = requestAnimationFrame(drawWaveform);
        }
      },
      [isPlaying, played, duration, waveformData],
    );

    useEffect(() => {
      if (isPlaying) {
        animFrameRef.current = requestAnimationFrame(drawWaveform);
      } else {
        cancelAnimationFrame(animFrameRef.current);
        drawWaveform(0);
      }
      return () => cancelAnimationFrame(animFrameRef.current);
    }, [isPlaying, drawWaveform]);

    useEffect(() => {
      const handleResize = () => drawWaveform(0);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [drawWaveform]);

    useEffect(() => {
      const timer = setTimeout(() => drawWaveform(0), 50);
      return () => clearTimeout(timer);
    }, []);

    const showControls = useCallback(() => {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (isPlaying) {
        hideTimerRef.current = setTimeout(
          () => setControlsVisible(false),
          2500,
        );
      }
    }, [isPlaying]);

    const togglePlay = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: !isPlaying });
      showControls();
    }, [videoPlayerState, isPlaying, onUpdatePlayerState, showControls]);

    const handleProgress = useCallback(
      ({ playedSeconds }: { playedSeconds: number }): void => {
        if (!seeking) {
          setPlayed(playedSeconds);
          onUpdatePlayerState({
            ...videoPlayerState,
            currentTime: playedSeconds,
          });
        }
      },
      [seeking, videoPlayerState, onUpdatePlayerState],
    );

    const handleDuration = useCallback((d: number) => setDuration(d), []);

    const handlePlay = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: true });
    }, [videoPlayerState, onUpdatePlayerState]);

    const handlePause = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
      setControlsVisible(true);
    }, [videoPlayerState, onUpdatePlayerState]);

    const handleEnded = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
      setControlsVisible(true);
    }, [videoPlayerState, onUpdatePlayerState]);

    const handleSliderChange = useCallback(
      (_: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        setPlayed(v);
        playerRef.current?.seekTo(v, "seconds");
      },
      [],
    );

    const handleSliderMouseDown = useCallback(() => setSeeking(true), []);
    const handleSliderChangeCommitted = useCallback(
      () => setSeeking(false),
      [],
    );

    useImperativeHandle(ref, () => ({
      seekTo: (time: number): void => {
        playerRef.current?.seekTo(time);
        setPlayed(time);
      },
      play: (): void => {
        const ip = playerRef.current?.getInternalPlayer();
        if (ip?.paused) {
          ip.play();
          onUpdatePlayerState({ ...videoPlayerState, isPlaying: true });
        }
      },
      pause: (): void => {
        const ip = playerRef.current?.getInternalPlayer();
        if (ip && !ip.paused) {
          ip.pause();
          onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
        }
      },
      isPlaying: (): boolean =>
        !playerRef.current?.getInternalPlayer()?.paused || false,
    }));

    return (
      <Box
        onMouseMove={showControls}
        onMouseEnter={showControls}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          background: "#ffffff",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: "12px",
          overflow: "hidden",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "none" }}>
          <ReactPlayer
            ref={playerRef}
            url={src}
            playing={isPlaying}
            muted={muted}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            width="0"
            height="0"
            controls={false}
          />
        </Box>

        {/* Waveform + icon area */}
        <Box
          onClick={togglePlay}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            pb: 6,
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: "94%",
              height: "45%",
              display: "block",
            }}
          />
        </Box>

        {/* Centered play button when paused */}
        {!isPlaying && (
          <Box
            onClick={togglePlay}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 20px rgba(37,99,235,0.5)",
                pointerEvents: "auto",
                cursor: "pointer",
                transition: "transform 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: "#1d4ed8",
                  transform: "scale(1.06)",
                },
              }}
            >
              <PlayArrowIcon sx={{ color: "#fff", fontSize: 32, ml: "3px" }} />
            </Box>
          </Box>
        )}

        {/* Bottom controls bar */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background:
              "linear-gradient(to top, rgba(255,255,255,0.95) 0%, transparent 100%)",
            px: 1.5,
            pt: 3,
            pb: 1,
            opacity: controlsVisible || !isPlaying ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        >
          <Slider
            value={played}
            min={0}
            max={duration || 1}
            step={0.1}
            onChange={handleSliderChange}
            onMouseDown={handleSliderMouseDown}
            onChangeCommitted={handleSliderChangeCommitted}
            sx={{
              color: "#2563eb",
              height: 3,
              padding: "6px 0",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                transition: "0.1s",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(37,99,235,0.25)",
                },
              },
              "& .MuiSlider-rail": {
                opacity: 0.2,
                backgroundColor: "#94a3b8",
              },
              "& .MuiSlider-track": {
                border: "none",
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
            }}
          >
            <IconButton
              onClick={togglePlay}
              size="small"
              sx={{ color: "#475569", p: 0.25 }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: 20 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>

            <Typography
              sx={{
                color: "#475569",
                fontSize: "12px",
                fontVariantNumeric: "tabular-nums",
                flex: 1,
                userSelect: "none",
                pl: 0.5,
              }}
            >
              {fmt(played)} / {fmt(duration)}
            </Typography>

            <IconButton
              onClick={() => setMuted((m) => !m)}
              size="small"
              sx={{ color: "#475569", p: 0.25 }}
            >
              {muted ? (
                <VolumeOffIcon sx={{ fontSize: 18 }} />
              ) : (
                <VolumeUpIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  },
);

AudioPreviewPlayer.displayName = "AudioPreviewPlayer";

export default AudioPreviewPlayer;
